<template>
  <v-card class="pl-5 pr-5 pb-5">
    <v-row>
      <v-toolbar flat>
        <div>
          <div class="headline pl-1">Marketing Sources</div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedOfficeId"
          :items="officeItems"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 220px"
          class="mr-4"
        />
        <v-menu
          v-model="monthMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-btn text v-on="on">{{ displayMonth }}</v-btn>
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @input="monthMenu = false"
          />
        </v-menu>
      </v-toolbar>
    </v-row>

    <v-progress-linear v-if="$apollo.loading" indeterminate color="primary" />

    <div>
      <!-- Summary Cards -->
      <v-row>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Total Retained</div>
            <div class="text-h4">{{ totalRetained }}</div>
            <div v-if="totalGoal" class="caption">
              Goal: {{ totalGoal }}
              <span v-if="isCurrentMonth">
                &middot; Target: {{ proratedTotalTarget }}</span
              >
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Total Spend</div>
            <div class="text-h4">{{ formatCurrency(totalSpend) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Avg Cost Per Case</div>
            <div class="text-h4">{{ formatCurrency(avgCostPerCase) }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="pa-3 text-center">
            <div class="subtitle-2 grey--text">Goal Progress</div>
            <div v-if="totalGoal" class="text-h4">{{ goalProgressPct }}%</div>
            <v-progress-linear
              v-if="totalGoal"
              :value="goalProgressPct"
              :color="goalProgressColor"
              height="8"
              class="mt-2"
            />
            <div v-if="!totalGoal" class="text-h4 grey--text">--</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Source Performance Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Source Performance
              <span
                v-if="selectedOfficeName"
                class="ml-2 subtitle-1 grey--text"
              >
                ({{ selectedOfficeName }})
              </span>
              <v-spacer />
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                class="shrink"
              />
            </v-card-title>
            <v-data-table
              :headers="tableHeaders"
              :items="sourceRows"
              :search="search"
              :sort-by="['retained']"
              :sort-desc="[true]"
              :loading="$apollo.loading"
              dense
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="item.statusColor" small dark>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.goal="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.editGoal"
                  @save="saveGoal(item)"
                  large
                >
                  {{ item.goal || "-" }}
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.editGoal"
                      label="Goal"
                      type="number"
                      single-line
                    />
                  </template>
                </v-edit-dialog>
              </template>
              <template v-slot:item.target="{ item }">
                <span v-if="item.target != null">{{ item.target }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:item.spend="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.editSpend"
                  @save="saveSpend(item)"
                  large
                >
                  {{ item.spend ? formatCurrency(item.spend) : "-" }}
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.editSpend"
                      label="Spend"
                      type="number"
                      prefix="$"
                      single-line
                    />
                  </template>
                </v-edit-dialog>
              </template>
              <template v-slot:item.costPerCase="{ item }">
                {{ item.costPerCase ? formatCurrency(item.costPerCase) : "-" }}
              </template>
              <template v-slot:item.trailing6Avg="{ item }">
                {{ item.trailing6Avg ? item.trailing6Avg.toFixed(1) : "-" }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Goals Management -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Manage Goals &amp; Spend ({{ displayMonth
              }}{{
                selectedOfficeName
                  ? " - " + selectedOfficeName
                  : " - All Offices"
              }})
            </v-card-title>
            <v-card-text>
              <!-- Overall Goal -->
              <v-row align="center">
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="overallGoalInput"
                    type="number"
                    label="Overall Monthly Retained Goal"
                    dense
                    outlined
                    @blur="saveOverallGoal"
                  />
                </v-col>
              </v-row>

              <v-divider class="mb-4" />

              <!-- Add new source goal -->
              <v-row align="center">
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="newGoalSource"
                    :items="availableSources"
                    item-text="contact"
                    item-value="contact"
                    label="Add source goal..."
                    dense
                    outlined
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="newGoalValue"
                    type="number"
                    label="Goal"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="newGoalSpend"
                    type="number"
                    label="Spend"
                    prefix="$"
                    dense
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn
                    color="primary"
                    :disabled="!newGoalSource || !newGoalValue"
                    @click="addSourceGoal"
                  >
                    Add Goal
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Existing goals table -->
              <v-data-table
                v-if="sourceGoalRows.length > 0"
                :headers="goalTableHeaders"
                :items="sourceGoalRows"
                dense
                class="mt-4"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.goal="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.editGoal"
                    @save="saveGoalFromManager(item)"
                    large
                  >
                    {{ item.goal }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.editGoal"
                        label="Goal"
                        type="number"
                        single-line
                      />
                    </template>
                  </v-edit-dialog>
                </template>
                <template v-slot:item.spend="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.editSpend"
                    @save="saveSpendFromManager(item)"
                    large
                  >
                    {{ item.spend ? formatCurrency(item.spend) : "-" }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.editSpend"
                        label="Spend"
                        type="number"
                        prefix="$"
                        single-line
                      />
                    </template>
                  </v-edit-dialog>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click="deleteGoal(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              <div
                v-if="sourceGoalRows.length === 0"
                class="text-center pa-4 grey--text"
              >
                No source goals set for {{ displayMonth }}. Use the form above
                to add goals.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts -->
      <v-row>
        <v-col cols="12" sm="6">
          <v-card class="pa-3">
            <SourcesTrendChart :data="retainedBySourceByMonth" />
          </v-card>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card class="pa-3">
            <SourcesGoalChart :sourceRows="sourceRowsWithGoals" />
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { format, parseISO, getDaysInMonth, getDate } from "date-fns";
import SourcesTrendChart from "@/charts/SourcesTrendChart.vue";
import SourcesGoalChart from "@/charts/SourcesGoalChart.vue";

const OFFICES_QUERY = gql`
  query CaseOffices {
    caseOffices {
      officeId
      office
    }
  }
`;

const RETAINED_BY_SOURCE_QUERY = gql`
  query RetainedBySourceByMonth($monthsBack: Int, $officeId: Int) {
    retainedBySourceByMonth(monthsBack: $monthsBack, officeId: $officeId) {
      sourceName
      month
      year
      count
    }
  }
`;

const GOALS_QUERY = gql`
  query MarketingSourceGoals($month: String, $officeName: String) {
    marketingSourceGoals(month: $month, officeName: $officeName) {
      id
      sourceName
      month
      goal
      spend
      officeName
    }
  }
`;

const ADVERTISING_SOURCES_QUERY = gql`
  query AdvertisingSources {
    advertisingSources {
      contactId
      contact
    }
  }
`;

const UPSERT_GOAL = gql`
  mutation UpsertMarketingSourceGoal($goal: MarketingSourceGoalInput!) {
    upsertMarketingSourceGoal(goal: $goal) {
      id
      sourceName
      month
      goal
      spend
      officeName
    }
  }
`;

const DELETE_GOAL = gql`
  mutation DeleteMarketingSourceGoal($id: ID!) {
    deleteMarketingSourceGoal(id: $id) {
      id
    }
  }
`;

export default {
  components: {
    SourcesTrendChart,
    SourcesGoalChart
  },
  data() {
    return {
      selectedMonth: format(new Date(), "yyyy-MM"),
      monthMenu: false,
      search: "",
      overallGoalInput: null,
      newGoalSource: null,
      newGoalValue: null,
      newGoalSpend: null,
      selectedOfficeId: null
    };
  },
  computed: {
    officeItems() {
      const items = [{ text: "All Offices", value: null }];
      if (this.caseOffices) {
        this.caseOffices.forEach(o => {
          items.push({ text: o.office, value: o.officeId });
        });
      }
      return items;
    },
    selectedOffice() {
      if (!this.selectedOfficeId || !this.caseOffices) return null;
      return (
        this.caseOffices.find(o => o.officeId === this.selectedOfficeId) || null
      );
    },
    selectedOfficeName() {
      return this.selectedOffice ? this.selectedOffice.office : null;
    },
    currentOfficeNameForGoals() {
      return this.selectedOfficeName || "__ALL__";
    },
    displayMonth() {
      return format(parseISO(this.selectedMonth + "-01"), "MMMM yyyy");
    },
    selectedMonthDate() {
      return this.selectedMonth + "-01";
    },
    selectedMonthNum() {
      return parseInt(this.selectedMonth.split("-")[1]);
    },
    selectedYearNum() {
      return parseInt(this.selectedMonth.split("-")[0]);
    },
    isCurrentMonth() {
      const now = new Date();
      return (
        this.selectedYearNum === now.getFullYear() &&
        this.selectedMonthNum === now.getMonth() + 1
      );
    },
    dayProgress() {
      if (!this.isCurrentMonth) return 1;
      const today = getDate(new Date());
      const daysInMonth = getDaysInMonth(new Date());
      return today / daysInMonth;
    },
    goalsMap() {
      if (!this.marketingSourceGoals) return {};
      let map = {};
      this.marketingSourceGoals.forEach(g => {
        map[g.sourceName] = g;
      });
      return map;
    },
    totalGoal() {
      const overall = this.goalsMap["__TOTAL__"];
      return overall ? overall.goal : null;
    },
    proratedTotalTarget() {
      if (!this.totalGoal) return null;
      return Math.round(this.totalGoal * this.dayProgress);
    },
    priorYearMonth() {
      return {
        month: this.selectedMonthNum,
        year: this.selectedYearNum - 1
      };
    },
    sourceData() {
      if (!this.retainedBySourceByMonth) return {};
      let map = {};
      this.retainedBySourceByMonth.forEach(row => {
        if (!row.sourceName) return;
        if (!map[row.sourceName]) map[row.sourceName] = [];
        map[row.sourceName].push(row);
      });
      return map;
    },
    sourceRows() {
      const sources = Object.keys(this.sourceData);
      return sources.map(name => {
        const rows = this.sourceData[name];
        const currentRow = rows.find(
          r =>
            r.month === this.selectedMonthNum && r.year === this.selectedYearNum
        );
        const priorYearRow = rows.find(
          r =>
            r.month === this.priorYearMonth.month &&
            r.year === this.priorYearMonth.year
        );

        // Trailing 6 month average (exclude current month)
        const sixMonthsAgo = new Date(
          this.selectedYearNum,
          this.selectedMonthNum - 1,
          1
        );
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const trailing = rows.filter(r => {
          if (
            r.month === this.selectedMonthNum &&
            r.year === this.selectedYearNum
          )
            return false;
          const rowDate = new Date(r.year, r.month - 1, 1);
          return rowDate >= sixMonthsAgo;
        });
        const trailing6Avg =
          trailing.length > 0
            ? trailing.reduce((sum, r) => sum + r.count, 0) / trailing.length
            : null;

        const retained = currentRow ? currentRow.count : 0;
        const goalEntry = this.goalsMap[name];
        const goal = goalEntry ? goalEntry.goal : null;
        const spend = goalEntry ? parseFloat(goalEntry.spend) || null : null;
        const costPerCase = spend && retained > 0 ? spend / retained : null;

        // Status calculation
        let status = "No Data";
        let statusColor = "grey";
        if (goal) {
          // Goal-based: prorate for current month, direct compare for past
          const target = goal * this.dayProgress;
          const pct = target > 0 ? (retained / target) * 100 : 0;
          if (pct >= 100) {
            status = "Exceeding";
            statusColor = "green";
          } else if (pct >= 80) {
            status = "On Track";
            statusColor = "orange";
          } else {
            status = "Lagging";
            statusColor = "red";
          }
        } else if (trailing6Avg) {
          // No goal: compare to 6-month average (prorated for current month)
          const target = trailing6Avg * this.dayProgress;
          const pct = target > 0 ? (retained / target) * 100 : 0;
          if (pct >= 100) {
            status = "Above Avg";
            statusColor = "green";
          } else if (pct >= 80) {
            status = "Near Avg";
            statusColor = "orange";
          } else {
            status = "Below Avg";
            statusColor = "red";
          }
        }

        const target = goal
          ? Math.round(goal * this.dayProgress)
          : trailing6Avg
          ? Math.round(trailing6Avg * this.dayProgress)
          : null;

        return {
          sourceName: name,
          retained,
          goal,
          editGoal: goal,
          target,
          priorYear: priorYearRow ? priorYearRow.count : 0,
          trailing6Avg,
          status,
          statusColor,
          spend,
          editSpend: spend,
          costPerCase
        };
      });
    },
    sourceRowsWithGoals() {
      return this.sourceRows.filter(r => r.goal);
    },
    totalRetained() {
      return this.sourceRows.reduce((sum, r) => sum + r.retained, 0);
    },
    totalSpend() {
      return this.sourceRows.reduce((sum, r) => sum + (r.spend || 0), 0);
    },
    avgCostPerCase() {
      if (this.totalRetained === 0) return 0;
      return this.totalSpend / this.totalRetained;
    },
    goalProgressPct() {
      if (!this.totalGoal) return 0;
      const prorated = this.totalGoal * this.dayProgress;
      return Math.min(Math.round((this.totalRetained / prorated) * 100), 999);
    },
    goalProgressColor() {
      if (this.goalProgressPct >= 100) return "green";
      if (this.goalProgressPct >= 80) return "orange";
      return "red";
    },
    tableHeaders() {
      return [
        { text: "Source", value: "sourceName" },
        { text: "Retained", value: "retained" },
        { text: "Goal", value: "goal" },
        { text: "Target", value: "target" },
        { text: "Prior Year", value: "priorYear" },
        { text: "6mo Avg", value: "trailing6Avg" },
        { text: "Status", value: "status" },
        { text: "Spend", value: "spend" },
        { text: "Cost/Case", value: "costPerCase" }
      ];
    },
    availableSources() {
      if (!this.advertisingSources) return [];
      return this.advertisingSources.filter(s => s.contact);
    },
    sourceGoalRows() {
      if (!this.marketingSourceGoals) return [];
      return this.marketingSourceGoals
        .filter(g => g.sourceName !== "__TOTAL__")
        .map(g => ({
          id: g.id,
          sourceName: g.sourceName,
          goal: g.goal,
          editGoal: g.goal,
          spend: parseFloat(g.spend) || null,
          editSpend: parseFloat(g.spend) || null
        }));
    },
    goalTableHeaders() {
      return [
        { text: "Source", value: "sourceName" },
        { text: "Goal", value: "goal" },
        { text: "Spend", value: "spend" },
        { text: "", value: "actions", sortable: false, width: "50px" }
      ];
    }
  },
  watch: {
    marketingSourceGoals() {
      const overall = this.goalsMap["__TOTAL__"];
      this.overallGoalInput = overall ? overall.goal : null;
    }
  },
  methods: {
    goalPayload(overrides) {
      return Object.assign(
        { officeName: this.currentOfficeNameForGoals },
        overrides
      );
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) return "$0";
      return (
        "$" +
        Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      );
    },
    async saveGoal(item) {
      const goalVal = parseInt(item.editGoal);
      if (isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: item.sourceName,
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: item.spend ? String(item.spend) : null
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveSpend(item) {
      const spendVal = parseFloat(item.editSpend);
      if (isNaN(spendVal) || spendVal < 0) return;
      const goal = item.goal || 0;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: item.sourceName,
            month: this.selectedMonthDate,
            goal: goal,
            spend: String(spendVal)
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveOverallGoal() {
      const goalVal = parseInt(this.overallGoalInput);
      if (isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: "__TOTAL__",
            month: this.selectedMonthDate,
            goal: goalVal
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async addSourceGoal() {
      const goalVal = parseInt(this.newGoalValue);
      if (!this.newGoalSource || isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: this.newGoalSource,
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: this.newGoalSpend ? String(this.newGoalSpend) : null
          })
        }
      });
      this.newGoalSource = null;
      this.newGoalValue = null;
      this.newGoalSpend = null;
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveGoalFromManager(item) {
      const goalVal = parseInt(item.editGoal);
      if (isNaN(goalVal) || goalVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: item.sourceName,
            month: this.selectedMonthDate,
            goal: goalVal,
            spend: item.spend ? String(item.spend) : null
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async saveSpendFromManager(item) {
      const spendVal = parseFloat(item.editSpend);
      if (isNaN(spendVal) || spendVal < 0) return;
      await this.$apollo.mutate({
        mutation: UPSERT_GOAL,
        variables: {
          goal: this.goalPayload({
            sourceName: item.sourceName,
            month: this.selectedMonthDate,
            goal: item.goal,
            spend: String(spendVal)
          })
        }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    },
    async deleteGoal(item) {
      await this.$apollo.mutate({
        mutation: DELETE_GOAL,
        variables: { id: item.id }
      });
      this.$apollo.queries.marketingSourceGoals.refetch();
    }
  },
  apollo: {
    caseOffices: {
      query: OFFICES_QUERY
    },
    retainedBySourceByMonth: {
      query: RETAINED_BY_SOURCE_QUERY,
      variables() {
        return {
          monthsBack: 13,
          officeId: this.selectedOfficeId
        };
      }
    },
    marketingSourceGoals: {
      query: GOALS_QUERY,
      variables() {
        return {
          month: this.selectedMonthDate,
          officeName: this.currentOfficeNameForGoals
        };
      }
    },
    advertisingSources: {
      query: ADVERTISING_SOURCES_QUERY
    }
  }
};
</script>

<style scoped></style>
